import { useCallback, useEffect, useRef, useState } from 'react';

import {
  FORM_BLOCK_BACKGROUND_VALUES,
  FORM_BLOCK_HEADING_ALIGNMENT_VALUES,
  type FormBlockBackground,
  type FormBlockHeadingAlignment,
} from '@/components/blocks/primitives/form-block';
import { type FormOption, useForms } from '@/hooks/use-forms';
import { type FormBlockResolvedForm, useResolvedFormBlock } from '@/lib/blocks/form-hydration';
import { type GroupChildFloat, getBlockFloat } from '@/lib/blocks/layout/group-layout';
import { pickStringUnion, readString } from '@/lib/prop-utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { PageBlock } from '@/types/block';

export const FORM_PREVIEW_DEBOUNCE_MS = 300;

export interface FormBlockDraft {
  formId?: string;
  form?: FormBlockResolvedForm;
  submitLabel?: string;
  background?: FormBlockBackground;
  headingAlignment?: FormBlockHeadingAlignment;
  float?: GroupChildFloat | null;
}

function draftFromProps(props: Record<string, unknown>): FormBlockDraft {
  return {
    formId: readString(props.formId) || undefined,
    form: props.form as FormBlockDraft['form'],
    submitLabel: readString(props.submitLabel) || undefined,
    background: pickStringUnion(
      readString(props.background),
      FORM_BLOCK_BACKGROUND_VALUES,
      'white',
    ),
    headingAlignment: pickStringUnion(
      readString(props.headingAlignment),
      FORM_BLOCK_HEADING_ALIGNMENT_VALUES,
      'left',
    ),
    float: getBlockFloat({ props }),
  };
}

function buildFormProps(
  originalProps: Record<string, unknown> | undefined,
  { formId, form, submitLabel, background, headingAlignment, float }: FormBlockDraft,
): Record<string, unknown> {
  const props: Record<string, unknown> = {
    ...(originalProps ?? {}),
    submitLabel,
    background,
    headingAlignment,
  };
  // Title/description are managed exclusively in Form Builder; clear any page-level overrides.
  delete props.title;
  delete props.description;
  if (formId) props.formId = formId;
  else delete props.formId;
  if (form) props.form = form;
  else delete props.form;
  if (float) props.float = float;
  else delete props.float;
  return props;
}

interface UseBlockFormDraftSyncOptions {
  sectionId: string;
  block: PageBlock;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function useBlockFormDraftSync({
  sectionId,
  block,
  open,
  onOpenChange,
}: UseBlockFormDraftSyncOptions) {
  const updateBlock = usePageEditorStore((state) => state.updateBlock);
  const previewBlockPropsWithFloatLayout = usePageEditorStore(
    (state) => state.previewBlockPropsWithFloatLayout,
  );
  const cancelBlockPropsWithFloatLayoutPreview = usePageEditorStore(
    (state) => state.cancelBlockPropsWithFloatLayoutPreview,
  );
  const { forms, loading } = useForms();

  const resolvedForm = useResolvedFormBlock({
    form: block.props?.form as FormBlockResolvedForm | undefined,
    formId: readString(block.props?.formId) || undefined,
  });

  const [draft, setDraft] = useState<FormBlockDraft>(() => draftFromProps(block.props || {}));
  const originalPropsRef = useRef<Record<string, unknown> | undefined>(undefined);
  const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingPreview = useCallback(() => {
    if (previewTimeoutRef.current !== null) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }
  }, []);

  const cancelPreview = useCallback(() => {
    clearPendingPreview();
    cancelBlockPropsWithFloatLayoutPreview(sectionId, block.id);
  }, [block.id, cancelBlockPropsWithFloatLayoutPreview, clearPendingPreview, sectionId]);

  const schedulePreview = useCallback(
    (nextDraft: FormBlockDraft) => {
      clearPendingPreview();
      previewTimeoutRef.current = setTimeout(() => {
        const nextProps = buildFormProps(originalPropsRef.current, nextDraft);
        previewBlockPropsWithFloatLayout(sectionId, block.id, nextProps);
        previewTimeoutRef.current = null;
      }, FORM_PREVIEW_DEBOUNCE_MS);
    },
    [block.id, clearPendingPreview, previewBlockPropsWithFloatLayout, sectionId],
  );

  useEffect(() => {
    if (open) {
      if (originalPropsRef.current === undefined) {
        originalPropsRef.current = block.props ? { ...block.props } : undefined;
        setDraft(draftFromProps(block.props || {}));
      }
    } else {
      cancelPreview();
      originalPropsRef.current = undefined;
    }
  }, [open, block.props, cancelPreview]);

  useEffect(() => {
    if (!open || !resolvedForm) return;

    setDraft((current) => {
      if (current.formId === resolvedForm.id && current.form) return current;

      const next = {
        ...current,
        formId: resolvedForm.id,
        form: current.form ?? { ...resolvedForm },
      };
      schedulePreview(next);
      return next;
    });
  }, [open, resolvedForm, schedulePreview]);

  // Clean up on unmount.
  useEffect(() => () => cancelPreview(), [cancelPreview]);

  const commitDraft = useCallback(
    (next: FormBlockDraft) => {
      const nextProps = buildFormProps(originalPropsRef.current, next);
      schedulePreview(next);
      updateBlock(sectionId, block.id, { props: nextProps });
    },
    [block.id, schedulePreview, sectionId, updateBlock],
  );

  const updateDraft = useCallback(
    (patch: Partial<FormBlockDraft>) => {
      setDraft((current) => {
        const next = { ...current, ...patch };
        commitDraft(next);
        return next;
      });
    },
    [commitDraft],
  );

  const handleFormChange = useCallback(
    (formId: string) => {
      const selected = forms.find((f: FormOption) => f.id === formId);
      setDraft((current) => {
        const next = {
          ...current,
          formId: formId || undefined,
          form: selected
            ? {
                id: selected.id,
                title: selected.title,
                description: selected.description,
                fields: selected.fields,
                settings: selected.settings,
                thirdParty: selected.thirdParty,
              }
            : undefined,
        };
        commitDraft(next);
        return next;
      });
    },
    [commitDraft, forms],
  );

  const handleClose = useCallback(() => {
    cancelPreview();
    onOpenChange(false);
  }, [cancelPreview, onOpenChange]);

  const formOptions = loading
    ? []
    : forms.map((f: FormOption) => ({ value: f.id, title: f.title }));

  return {
    draft,
    forms,
    loading,
    formOptions,
    updateDraft,
    handleFormChange,
    handleClose,
  };
}
